import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AutoAwesome,
  Close,
  DeleteOutline,
  Description,
  Edit,
  LockReset,
  MailOutline,
  PersonOutline,
  PhotoCamera,
  PriceChange,
  Save,
  Security,
  Send,
  UploadFile,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import * as authMod from "firebase/auth";

import { FirebaseAuth, ModularAuth, Storage } from "../../FirebaseAuth/firebase";
import ContractorProfileContent from "../../ContractorProfileContent";
import Loader from "../../Loader";
import { recordUserActivity } from "../../../libs/activityLog";
import { formatServiceLevelSummary } from "../../../libs/labels";
import servicesJSON from "../../../inc/services.json";

const formatTimestamp = (time) => {
  if (!time) return { date: "-", clock: "" };

  const dt = typeof time.toDate === "function" ? time.toDate() : new Date(time);
  if (Number.isNaN(dt.getTime())) return { date: "-", clock: "" };

  return {
    date: dt.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }),
    clock: dt.toLocaleTimeString("en-GB", {
      hour12: false,
      timeZone: "UTC",
    }),
  };
};
const cleanAuthError = (error, fallback = "Something went wrong.") => {
  const raw = String(error?.message || "").trim();
  if (!raw) return fallback;
  const withoutPrefix = raw.replace(/^Firebase:\s*/i, "");
  const withoutCode = withoutPrefix.replace(/\s*\(auth\/[^)]+\)\.?$/i, "");
  return withoutCode || fallback;
};
const maskMfaPhone = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";
  return `***${digits.slice(-4)}`;
};
const readField = (row = {}, snakeKey, fallback = undefined) => {
  if (row && Object.prototype.hasOwnProperty.call(row, snakeKey)) return row[snakeKey];
  return fallback;
};
const normalizeGeneratedProfile = (id, data = {}) => {
  const aiMeta = readField(data, "ai_meta", {}) || {};
  return {
    id,
    ...data,
    connectAccountId: readField(data, "connect_account_id", ""),
    displayName: readField(data, "display_name", ""),
    needsRefresh: Boolean(readField(data, "needs_refresh", false)),
    refreshReason: readField(data, "refresh_reason", ""),
    lastGeneratedAt: readField(data, "last_generated_at", null),
    generatedResume: readField(data, "generated_resume", ""),
    generatedResumeHtml: readField(data, "generated_resume_html", ""),
    experienceSummary: readField(data, "experience_summary", []),
    domainFocus: readField(data, "domain_focus", []),
    sourceStats: readField(data, "source_stats", {}),
    aiMeta: {
      ...aiMeta,
      promptVersion: readField(aiMeta, "prompt_version", ""),
      sourceHash: readField(aiMeta, "source_hash", ""),
      generatedAt: readField(aiMeta, "generated_at", null),
      lastCheckedAt: readField(aiMeta, "last_checked_at", null),
    },
    updatedAt: readField(data, "updated_at", null),
  };
};

const findServiceLevelConfig = (serviceCode, levelCode) => {
  const jobs = servicesJSON?.jobs || [];
  const service = jobs.find((item) => item.code === serviceCode);
  if (!service) return null;
  const level = (service.levels || []).find((item) => item.code === levelCode);
  if (!level) return null;
  return {
    serviceLabel: service.label || serviceCode,
    levelLabel: level.label || levelCode,
    minHourly: Number(level.minPrice || 0),
    maxHourly: Number(level.maxPrice || 0),
    currency: String(level.currency || "usd").toLowerCase(),
  };
};

const formatMoneyFromCents = (amountInCents = 0, currency = "usd") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: String(currency || "usd").toUpperCase(),
  }).format(Number(amountInCents || 0) / 100);

const RESUME_CONTENT_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const formatFileSize = (bytes = 0) => {
  const size = Number(bytes || 0);
  if (!size) return "";
  if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const UserProfileView = ({ section = "profile" }) => {
  const history = useHistory();
  const currentUser = ModularAuth.currentUser;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const [displayName, setDisplayName] = useState(currentUser?.displayName || "");
  const [emailVerified, setEmailVerified] = useState(currentUser?.emailVerified || false);
  const [hasOpenInvoices, setHasOpenInvoices] = useState(false);
  const [checkingOpenInvoices, setCheckingOpenInvoices] = useState(true);
  const [pricingProfile, setPricingProfile] = useState({
    role: "",
    connectAccountId: "",
    service: "",
    serviceLabel: "",
    level: "",
    levelLabel: "",
    currency: "usd",
    currentHourlyCents: 0,
    minHourly: 0,
    maxHourly: 0,
    pendingRequest: null,
  });
  const [pricingDraft, setPricingDraft] = useState({
    service: "",
    level: "",
    hourlyRate: "",
  });
  const [submittingPricingRequest, setSubmittingPricingRequest] = useState(false);
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [contractorProfileLoading, setContractorProfileLoading] = useState(false);
  const [contractorGeneratedProfile, setContractorGeneratedProfile] = useState(null);
  const [resumeProfile, setResumeProfile] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [uploadingResume, setUploadingResume] = useState(false);

  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(currentUser?.photoURL || "");

  const [dialogType, setDialogType] = useState(null);
  const [dialogStep, setDialogStep] = useState("verify");
  const [passwordInput, setPasswordInput] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [confirmValue, setConfirmValue] = useState("");
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaHintIndex, setMfaHintIndex] = useState(0);
  const [mfaVerificationId, setMfaVerificationId] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaInfo, setMfaInfo] = useState("");
  const [mfaError, setMfaError] = useState("");
  const [sendingMfaCode, setSendingMfaCode] = useState(false);
  const mfaRecaptchaRef = useRef(null);

  const [sendingVerification, setSendingVerification] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const showMessage = (message, severity = "success") => {
    setSnackbar({ open: true, severity, message });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const serviceOptions = servicesJSON?.jobs || [];
  const selectedDraftService = serviceOptions.find((item) => item.code === pricingDraft.service) || null;
  const draftLevelOptions = selectedDraftService?.levels || [];
  const selectedDraftLevel = draftLevelOptions.find((item) => item.code === pricingDraft.level) || null;
  const draftMinHourly = Number(selectedDraftLevel?.minPrice || pricingProfile.minHourly || 0);
  const draftMaxHourly = Number(selectedDraftLevel?.maxPrice || pricingProfile.maxHourly || 0);
  const draftCurrency = String(selectedDraftLevel?.currency || pricingProfile.currency || "usd").toLowerCase();

  const createActivityLog = async (action, description = "") => {
    await recordUserActivity(action, description);
  };

  const refreshUserState = async () => {
    const authUser = ModularAuth.currentUser;
    if (!authUser) {
      history.push("/signin");
      return;
    }

    await authUser.reload();

    setDisplayName(authUser.displayName || "");
    setAvatarPreview(authUser.photoURL || "");
    setEmailVerified(!!authUser.emailVerified);

    try {
      authMod.multiFactor(authUser);
    } catch (_error) {
      // MFA state is managed on the dedicated MFA page.
    }
  };

  const refreshPricingState = async () => {
    const authUser = ModularAuth.currentUser;
    if (!authUser) return;

    const userDoc = await FirebaseAuth.firestore().collection("users").doc(authUser.uid).get();
    if (!userDoc.exists) return;

    const data = userDoc.data() || {};
    const metadata = data.metadata || {};
    const connectAccountId = String(data.connect_id || "");
    const serviceCode = String(metadata.service || "");
    const levelCode = String(metadata.level || "");
    const levelConfig = findServiceLevelConfig(serviceCode, levelCode);
    const currentHourlyCents = Number(data.unit_price || metadata.price || 0);
    const pendingRequest = data.pricing_update_request || null;
    const currency = levelConfig?.currency || String(metadata.currency || "usd").toLowerCase();
    const minHourly = Number(levelConfig?.minHourly || 0);
    const maxHourly = Number(levelConfig?.maxHourly || 0);

    setPricingProfile({
      role: String(data.role || ""),
      connectAccountId,
      service: serviceCode,
      serviceLabel: String(levelConfig?.serviceLabel || serviceCode || ""),
      level: levelCode,
      levelLabel: String(levelConfig?.levelLabel || levelCode || ""),
      currency,
      currentHourlyCents,
      minHourly,
      maxHourly,
      pendingRequest,
    });
    setResumeProfile(data.resume || null);

    if (String(data.role || "").toLowerCase() === "contractor") {
      setContractorProfileLoading(true);
      try {
        const profileDoc = await FirebaseAuth.firestore().collection("profile").doc(authUser.uid).get();
        setContractorGeneratedProfile(
          profileDoc.exists ? normalizeGeneratedProfile(profileDoc.id, profileDoc.data() || {}) : null
        );
      } catch (_error) {
        setContractorGeneratedProfile(null);
      } finally {
        setContractorProfileLoading(false);
      }
    } else {
      setContractorGeneratedProfile(null);
      setContractorProfileLoading(false);
    }

  };

  const refreshOpenInvoiceState = async () => {
    const authUser = ModularAuth.currentUser;
    if (!authUser) {
      setHasOpenInvoices(false);
      setCheckingOpenInvoices(false);
      return;
    }

    setCheckingOpenInvoices(true);
    try {
      const snapshot = await FirebaseAuth
        .firestore()
        .collection("users")
        .doc(authUser.uid)
        .collection("invoices")
        .where("status", "==", "open")
        .limit(1)
        .get();

      setHasOpenInvoices(!snapshot.empty);
    } catch (_error) {
      setHasOpenInvoices(false);
    } finally {
      setCheckingOpenInvoices(false);
    }
  };

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        await refreshUserState();
        await refreshPricingState();
        await refreshOpenInvoiceState();
      } finally {
        setLoading(false);
      }
    };

    run();
    // Initial profile load only; refresh helpers are also invoked explicitly after user actions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!avatarFile) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(String(reader.result || ""));
    };
    reader.readAsDataURL(avatarFile);
  }, [avatarFile]);

  const openDialog = (type) => {
    setDialogType(type);
    setDialogStep(type === "name" ? "edit" : "verify");
    setPasswordInput("");
    setInputValue(type === "name" ? displayName : "");
    setConfirmValue("");
    setMfaResolver(null);
    setMfaHintIndex(0);
    setMfaVerificationId("");
    setMfaCode("");
    setMfaInfo("");
    setMfaError("");
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogType(null);
    setDialogStep("verify");
    setPasswordInput("");
    setInputValue("");
    setConfirmValue("");
    setMfaResolver(null);
    setMfaHintIndex(0);
    setMfaVerificationId("");
    setMfaCode("");
    setMfaInfo("");
    setMfaError("");
    if (mfaRecaptchaRef.current) {
      mfaRecaptchaRef.current.clear();
      mfaRecaptchaRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (mfaRecaptchaRef.current) {
        mfaRecaptchaRef.current.clear();
        mfaRecaptchaRef.current = null;
      }
    };
  }, []);

  const ensureMfaRecaptcha = async () => {
    if (mfaRecaptchaRef.current) {
      return mfaRecaptchaRef.current;
    }
    const verifier = new authMod.RecaptchaVerifier(ModularAuth, "profile-mfa-reauth-recaptcha", {
      size: "normal",
    });
    await verifier.render();
    mfaRecaptchaRef.current = verifier;
    return verifier;
  };

  const sendReauthMfaCode = async () => {
    if (!mfaResolver) return;
    const hint = mfaResolver.hints?.[mfaHintIndex];
    if (!hint) {
      setMfaError("No MFA factor is available.");
      return;
    }

    setSendingMfaCode(true);
    setMfaError("");
    setMfaInfo("");
    try {
      if (hint.factorId === authMod.PhoneMultiFactorGenerator.FACTOR_ID) {
        const verifier = await ensureMfaRecaptcha();
        const provider = new authMod.PhoneAuthProvider(ModularAuth);
        const verificationId = await provider.verifyPhoneNumber(
          {
            multiFactorHint: hint,
            session: mfaResolver.session,
          },
          verifier
        );
        setMfaVerificationId(verificationId);
        setMfaInfo("Verification code sent.");
      } else if (hint.factorId === "totp" && authMod.TotpMultiFactorGenerator?.assertionForSignIn) {
        setMfaVerificationId("totp-ready");
        setMfaInfo("Open your authenticator app and enter the current code.");
      } else {
        throw new Error("Unsupported MFA factor type.");
      }
    } catch (error) {
      setMfaError(cleanAuthError(error, "Failed to send MFA challenge."));
    } finally {
      setSendingMfaCode(false);
    }
  };

  const verifyReauthMfaCode = async () => {
    if (!mfaResolver) return;
    const hint = mfaResolver.hints?.[mfaHintIndex];
    if (!hint) {
      setMfaError("No MFA factor is selected.");
      return;
    }
    if (!mfaCode) {
      setMfaError("Enter verification code.");
      return;
    }

    setSaving(true);
    setMfaError("");
    try {
      let assertion;
      if (hint.factorId === authMod.PhoneMultiFactorGenerator.FACTOR_ID) {
        if (!mfaVerificationId) {
          throw new Error("Send verification code first.");
        }
        const credential = authMod.PhoneAuthProvider.credential(mfaVerificationId, mfaCode);
        assertion = authMod.PhoneMultiFactorGenerator.assertion(credential);
      } else if (hint.factorId === "totp" && authMod.TotpMultiFactorGenerator?.assertionForSignIn) {
        assertion = authMod.TotpMultiFactorGenerator.assertionForSignIn(hint.uid, mfaCode);
      } else {
        throw new Error("Unsupported MFA factor type.");
      }

      await mfaResolver.resolveSignIn(assertion);
      const freshUser = ModularAuth.currentUser;
      if (freshUser) {
        await freshUser.getIdToken(true);
        await freshUser.reload();
      }
      closeDialog();
      history.push("/mfa/enroll");
    } catch (error) {
      setMfaError(cleanAuthError(error, "Invalid verification code."));
    } finally {
      setSaving(false);
    }
  };

  const verifySensitiveAction = async () => {
    const authUser = ModularAuth.currentUser;
    if (!authUser || !authUser.email) return;

    setSaving(true);
    try {
      const credential = authMod.EmailAuthProvider.credential(authUser.email, passwordInput);
      await authMod.reauthenticateWithCredential(authUser, credential);

      if (dialogType === "reauth_mfa") {
        await authUser.getIdToken(true);
        await authUser.reload();
        closeDialog();
        history.push("/mfa/enroll");
        return;
      }

      setDialogStep("edit");
    } catch (error) {
      if (dialogType === "reauth_mfa" && error?.code === "auth/multi-factor-auth-required") {
        try {
          const resolver = authMod.getMultiFactorResolver(ModularAuth, error);
          const firstHint = resolver.hints?.[0] || null;
          setMfaResolver(resolver);
          setMfaHintIndex(0);
          setMfaVerificationId(firstHint?.factorId === "totp" ? "totp-ready" : "");
          setMfaCode("");
          setMfaInfo(firstHint?.factorId === "totp" ? "Enter the current code from your authenticator app." : "Multi-factor verification is required to continue.");
          setMfaError("");
          return;
        } catch (_resolverError) {
          showMessage("Failed to initialize MFA challenge.", "error");
          return;
        }
      }
      showMessage(cleanAuthError(error, "Re-authentication failed"), "error");
    } finally {
      setSaving(false);
    }
  };

  const persistUserDoc = async (changes) => {
    const authUser = ModularAuth.currentUser;
    if (!authUser) return;

    const normalizedChanges = {
      ...changes,
      updated_at: new Date(),
    };

    if (Object.prototype.hasOwnProperty.call(normalizedChanges, "photoURL")) {
      normalizedChanges.photo_url = normalizedChanges.photoURL;
      delete normalizedChanges.photoURL;
    }

    await FirebaseAuth
      .firestore()
      .collection("users")
      .doc(authUser.uid)
      .set(normalizedChanges, { merge: true });
  };

  const handleSaveDialog = async () => {
    const authUser = ModularAuth.currentUser;
    if (!authUser) return;

    setSaving(true);
    try {
      if (dialogType === "name") {
        await authMod.updateProfile(authUser, { displayName: inputValue.trim() });
        await persistUserDoc({ display_name: inputValue.trim() });
        await createActivityLog("user name updated", "User updated display name");
        setDisplayName(inputValue.trim());
        showMessage("Name updated");
      }

      if (dialogType === "email") {
        await authMod.updateEmail(authUser, inputValue.trim());
        await persistUserDoc({ email: inputValue.trim() });
        await createActivityLog("email address updated", "User updated email address");
        showMessage("Email updated");
      }

      if (dialogType === "password") {
        if (!inputValue || inputValue.length < 8) {
          throw new Error("Password must be at least 8 characters");
        }
        if (inputValue !== confirmValue) {
          throw new Error("Passwords do not match");
        }

        await authMod.updatePassword(authUser, inputValue);
        await createActivityLog("password updated", "User updated password");
        showMessage("Password updated");
      }

      if (dialogType === "delete") {
        if (normalizedRole === "admin") {
          throw new Error("Admin accounts cannot be self-deleted");
        }

        const openInvoiceSnapshot = await FirebaseAuth
          .firestore()
          .collection("users")
          .doc(authUser.uid)
          .collection("invoices")
          .where("status", "==", "open")
          .limit(1)
          .get();

        if (!openInvoiceSnapshot.empty) {
          throw new Error("You have open invoices. Resolve them before deleting your account");
        }

        if (inputValue.trim().toLowerCase() !== "delete") {
          throw new Error('Type "delete" to confirm account deletion');
        }

        await authMod.deleteUser(authUser);
        showMessage("Account deleted");
        closeDialog();
        history.push("/login");
        return;
      }

      closeDialog();
      await refreshUserState();
      await refreshOpenInvoiceState();
    } catch (error) {
      showMessage(cleanAuthError(error, "Update failed"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async () => {
    const authUser = ModularAuth.currentUser;
    if (!authUser || !avatarFile) return;

    setUploadingAvatar(true);
    try {
      const path = `avatars/${authUser.uid}/${Date.now()}-${avatarFile.name}`;
      const storageRef = Storage.ref(path);

      await storageRef.put(avatarFile);
      const url = await storageRef.getDownloadURL();

      await authMod.updateProfile(authUser, { photoURL: url });
      await persistUserDoc({ photoURL: url });
      await createActivityLog("profile photo updated", "User updated profile photo");

      setAvatarFile(null);
      setAvatarPreview(url);
      showMessage("Profile photo updated");
    } catch (error) {
      showMessage(cleanAuthError(error, "Photo upload failed"), "error");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleResumeUpload = async (selectedFile = resumeFile) => {
    const authUser = ModularAuth.currentUser;
    if (!authUser || !selectedFile) return;

    if (!RESUME_CONTENT_TYPES.includes(selectedFile.type)) {
      showMessage("Upload a PDF or DOCX resume.", "error");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      showMessage("Resume must be 10 MB or smaller.", "error");
      return;
    }

    setUploadingResume(true);
    setResumeFile(selectedFile);
    try {
      const safeName = selectedFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `resumes/${authUser.uid}/current/${Date.now()}-${safeName}`;
      const storageRef = Storage.ref(path);
      const uploadedAt = new Date();
      const metadata = {
        storage_path: path,
        file_name: selectedFile.name,
        content_type: selectedFile.type,
        size: selectedFile.size,
        uploaded_at: uploadedAt,
        status: "processing",
        updated_at: uploadedAt,
      };

      await storageRef.put(selectedFile, { contentType: selectedFile.type });
      await persistUserDoc({ resume: metadata });
      await createActivityLog("resume uploaded", `Uploaded resume: ${selectedFile.name}`);

      setResumeProfile(metadata);
      const fn = FirebaseAuth.functions().httpsCallable("generateContractorProfile");
      await fn({ force: true });
      await refreshPricingState();
      await createActivityLog("profile regenerated", "Generated contractor profile from resume");

      setResumeFile(null);
      showMessage("Resume uploaded and work profile updated.");
    } catch (error) {
      await refreshPricingState();
      showMessage("Something went wrong while generating your work profile. Please try again.", "error");
    } finally {
      setUploadingResume(false);
    }
  };

  const handleResendVerification = async () => {
    const authUser = ModularAuth.currentUser;
    if (!authUser) return;

    setSendingVerification(true);
    try {
      await authMod.sendEmailVerification(authUser);
      showMessage(`Verification email sent to ${authUser.email}`);
    } catch (error) {
      showMessage(cleanAuthError(error, "Could not send verification email"), "error");
    } finally {
      setSendingVerification(false);
    }
  };

  const handleSubmitPricingRequest = async () => {
    const authUser = ModularAuth.currentUser;
    if (!authUser) return;

    const requestedHourly = Number(pricingDraft.hourlyRate || 0);
    const requestedService = String(pricingDraft.service || "").trim();
    const requestedLevel = String(pricingDraft.level || "").trim();
    if (!requestedService || !requestedLevel) {
      showMessage("Select service and level.", "error");
      return;
    }
    if (!(requestedHourly > 0)) {
      showMessage("Enter a valid hourly rate.", "error");
      return;
    }
    if (draftMinHourly > 0 && requestedHourly < draftMinHourly) {
      showMessage(`Rate must be at least ${draftMinHourly}/hr for the selected level.`, "error");
      return;
    }
    if (draftMaxHourly > 0 && requestedHourly > draftMaxHourly) {
      showMessage(`Rate cannot exceed ${draftMaxHourly}/hr for the selected level.`, "error");
      return;
    }

    setSubmittingPricingRequest(true);
    try {
      const requestPricingUpdate = FirebaseAuth.functions().httpsCallable("requestContractorPricingUpdate");
      const response = await requestPricingUpdate({
        requested_hourly_rate: requestedHourly,
        requested_service: requestedService,
        requested_level: requestedLevel,
        requested_currency: draftCurrency,
      });
      const message = response?.data?.message || "Pricing request submitted for admin review.";
      await createActivityLog("pricing update requested", message);
      await refreshPricingState();
      setPricingDialogOpen(false);
      showMessage(message, "success");
    } catch (error) {
      showMessage(cleanAuthError(error, "Failed to submit pricing request"), "error");
    } finally {
      setSubmittingPricingRequest(false);
    }
  };

  const openPricingDialog = () => {
    const pending = pricingProfile.pendingRequest || {};
    const fallbackHourly = pricingProfile.currentHourlyCents > 0 ? (pricingProfile.currentHourlyCents / 100).toFixed(2) : "";
    const pendingHourly = pending.requestedHourlyCents ? (Number(pending.requestedHourlyCents) / 100).toFixed(2) : "";
    setPricingDraft({
      service: String(pending.requestedService || pending.service || pricingProfile.service || ""),
      level: String(pending.requestedLevel || pending.level || pricingProfile.level || ""),
      hourlyRate: pendingHourly || fallbackHourly,
    });
    setPricingDialogOpen(true);
  };

  const closePricingDialog = () => {
    if (submittingPricingRequest) return;
    setPricingDialogOpen(false);
  };

  if (loading) {
    return (
      <Loader variant="section" label="Loading profile..." />
    );
  }

  const authUser = ModularAuth.currentUser;

  if (!authUser) return null;

  const isSecuritySection = section === "security";
  const isProfileSection = !isSecuritySection;
  const requestedHourly = Number(pricingDraft.hourlyRate || 0);
  const rateTooLow = draftMinHourly > 0 && requestedHourly > 0 && requestedHourly < draftMinHourly;
  const rateTooHigh = draftMaxHourly > 0 && requestedHourly > 0 && requestedHourly > draftMaxHourly;
  const canRequestPricingUpdate = pricingProfile.role === "contractor"
    && !!pricingDraft.service
    && !!pricingDraft.level
    && requestedHourly > 0
    && !rateTooLow
    && !rateTooHigh;
  const normalizedRole = String(pricingProfile.role || "").toLowerCase();
  const deleteAccountDisabled = normalizedRole === "admin" || checkingOpenInvoices || hasOpenInvoices;
  const deleteAccountDisabledMessage = normalizedRole === "admin"
    ? "Admin accounts are managed by platform ownership and cannot be self-deleted."
    : checkingOpenInvoices
      ? "Checking open invoices before enabling account deletion."
      : hasOpenInvoices
        ? normalizedRole === "client"
          ? "Client accounts with open invoices cannot be deleted. Resolve open invoices first."
          : normalizedRole === "contractor"
            ? "Contractor accounts with open invoices cannot be deleted. Resolve open invoices first."
            : "Accounts with open invoices cannot be deleted. Resolve open invoices first."
        : "";
  const dialogTitle =
    dialogType === "reauth_mfa"
      ? "Manage MFA"
      : dialogType === "password"
        ? "Change Password"
        : dialogType === "delete"
          ? "Delete Account"
          : dialogType === "email"
            ? "Update Email Address"
            : "Update Full Name";
  const dialogDescription =
    dialogType === "reauth_mfa"
      ? "Confirm your identity before managing multi-factor authentication."
      : dialogType === "password"
        ? "For your security, confirm your current password before choosing a new one."
        : dialogType === "delete"
          ? "This action permanently removes your account after verification."
          : dialogType === "email"
            ? "Confirm your password before changing the email used for sign-in."
            : "Keep your profile name accurate for invoices, contracts, and account records.";
  const DialogHeaderIcon =
    dialogType === "email"
      ? MailOutline
      : dialogType === "password"
        ? LockReset
        : dialogType === "delete"
          ? DeleteOutline
          : dialogType === "reauth_mfa"
            ? Security
            : PersonOutline;
  const dialogPrimaryLabel =
    dialogType === "reauth_mfa" && mfaResolver
      ? "Verify"
      : dialogStep === "verify"
        ? "Continue"
        : dialogType === "delete"
          ? "Delete account"
          : dialogType === "password"
            ? "Change password"
            : "Save changes";

  return (
    <Box sx={{ width: "100%", textAlign: "left" }}>
      {!emailVerified && authUser.email && (
        <Alert
          severity="warning"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button variant="outlined"
              color="inherit"
              size="small"
              onClick={handleResendVerification}
              disabled={sendingVerification}
              sx={{ textTransform: "none" }}
            >
              {sendingVerification ? <Loader size={16} /> : "Resend link"}
            </Button>
          }
        >
          Please verify your email address: <strong>{authUser.email}</strong>
        </Alert>
      )}

      {isProfileSection && (
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", mb: 3 }}>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Account
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                <Box position="relative" display="inline-block">
                  <Avatar src={avatarPreview} sx={{ width: 96, height: 96 }} />
                  <Tooltip title="Change photo">
                    <IconButton
                      size="small"
                      component="label"
                      sx={{
                        position: "absolute",
                        bottom: -6,
                        right: -6,
                        bgcolor: "background.paper",
                        boxShadow: 1,
                      }}
                    >
                      <PhotoCamera fontSize="small" />
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        onChange={(e) => setAvatarFile(e.target.files?.[0] || null)}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box flex={1}>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
                      <Box>
                        <Typography variant="subtitle2">Full Name</Typography>
                        <Typography variant="subtitle1">{displayName || "-"}</Typography>
                      </Box>
                      <IconButton onClick={() => openDialog("name")}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Divider />
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
                      <Box>
                        <Typography variant="subtitle2">Email Address</Typography>
                        <Typography variant="subtitle1">{authUser.email || "-"}</Typography>
                      </Box>
                      <IconButton onClick={() => openDialog("email")}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>

              {avatarFile && (
                <Box mt={2}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Save />}
                    disabled={uploadingAvatar}
                    onClick={handleAvatarUpload}
                    sx={{ textTransform: "none" }}
                  >
                    {uploadingAvatar ? <Loader size={18} /> : "Save Photo"}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Paper>
      )}

      {isSecuritySection && (
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", mb: 3 }}>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Security
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box>
                      <Typography variant="subtitle2">Password</Typography>
                      <Typography variant="subtitle1" color="text.secondary">Update your sign-in password</Typography>
                    </Box>
                  </Stack>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => openDialog("password")}
                    sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                  >
                    Update
                  </Button>
                </Stack>

                <Divider />

                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box>
                      <Typography variant="subtitle2">Multi-factor Authentication</Typography>
                      <Typography variant="subtitle1" color="text.secondary">Add or manage an extra sign-in step</Typography>
                    </Box>
                  </Stack>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => history.push("/mfa/enroll")}
                    sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                  >
                    Manage MFA
                  </Button>
                </Stack>

                <Divider />

                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box>
                      <Typography variant="subtitle2" color="error.main">Delete Account</Typography>
                      <Typography variant="subtitle1" color="text.secondary">
                        {deleteAccountDisabled
                          ? deleteAccountDisabledMessage
                          : "Once you delete this account, there is no going back. Please be certain."}
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={deleteAccountDisabled}
                    onClick={() => openDialog("delete")}
                    sx={{ textTransform: "none" }}
                  >
                    Delete Account
                  </Button>
                </Stack>

              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Paper>
      )}

      {isProfileSection && pricingProfile.role === "contractor" && (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", mb: 3 }}>
          <Box p={2}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Work Profile
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage pricing, resume source, and the generated profile clients use for matching.
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ my: 2 }} />

            <Stack spacing={2}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Pricing
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "flex-end" }} spacing={1}>
                          <Typography variant="h5" sx={{ fontWeight: 800 }}>
                            {formatMoneyFromCents(pricingProfile.currentHourlyCents, pricingProfile.currency)}/hr
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ pb: { sm: 0.35 } }}>
                            {formatServiceLevelSummary(pricingProfile.service, pricingProfile.level)}
                          </Typography>
                        </Stack>
                      </Box>
                      <IconButton size="small" onClick={openPricingDialog}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Stack>

                    {pricingProfile.pendingRequest?.status === "pending" && (
                      <Alert severity="info">
                        Pending change: {formatServiceLevelSummary(
                          pricingProfile.pendingRequest.requestedService || pricingProfile.pendingRequest.service,
                          pricingProfile.pendingRequest.requestedLevel || pricingProfile.pendingRequest.level,
                        )} / {formatMoneyFromCents(pricingProfile.pendingRequest.requestedHourlyCents || 0, pricingProfile.currency)}/hr.
                        Submitting again will overwrite the pending request.
                      </Alert>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Profile
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Upload your latest resume to generate the profile used for matching.
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={uploadingResume ? <Loader size={16} /> : <UploadFile />}
                        component="label"
                        disabled={uploadingResume}
                        sx={{ textTransform: "none" }}
                      >
                        Upload resume
                        <input
                          hidden
                          type="file"
                          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={async (event) => {
                            const file = event.target.files?.[0] || null;
                            event.target.value = "";
                            if (file) {
                              await handleResumeUpload(file);
                            }
                          }}
                        />
                      </Button>
                    </Stack>

                    {!resumeProfile?.storage_path ? (
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 1.5,
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          bgcolor: "background.paper",
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 1.5,
                              bgcolor: "grey.100",
                              color: "text.secondary",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flex: "0 0 auto"
                            }}
                          >
                            <Description sx={{ fontSize: 22 }} />
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                              No resume uploaded
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Upload a PDF or DOCX resume to generate your work profile.
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ) : (
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 1.5,
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            bgcolor: "background.paper",
                          }}
                        >
                          {contractorProfileLoading || uploadingResume ? (
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Loader size={18} />
                              <Typography variant="body2" color="text.secondary">
                                {uploadingResume ? "Uploading resume and generating profile..." : "Loading generated profile..."}
                              </Typography>
                            </Stack>
                          ) : !contractorGeneratedProfile ? (
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                              <AutoAwesome color="primary" />
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                  Generating profile
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Your resume is uploaded. We will generate a matching profile from your skills, job functions, and work summary.
                                </Typography>
                              </Box>
                            </Stack>
                          ) : (
                            <Stack spacing={1.5}>
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                  Generated profile
                                </Typography>
                                {Boolean(contractorGeneratedProfile?.needsRefresh) && (
                                  <Chip size="small" color="warning" label="Refresh recommended" sx={{ borderRadius: 1 }} />
                                )}
                              </Stack>
                              <Typography variant="body2" color="text.secondary">
                                Last generated: {formatTimestamp(contractorGeneratedProfile?.aiMeta?.generatedAt || contractorGeneratedProfile?.updatedAt).date} {formatTimestamp(contractorGeneratedProfile?.aiMeta?.generatedAt || contractorGeneratedProfile?.updatedAt).clock}
                              </Typography>
                              <ContractorProfileContent
                                profile={contractorGeneratedProfile}
                                emptyMessage="Generated summary is unavailable."
                              />
                            </Stack>
                          )}
                        </Box>

                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            bgcolor: "background.paper",
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1.5,
                                bgcolor: (theme) => theme.palette.primary.main + "14",
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flex: "0 0 auto",
                              }}
                            >
                              <Description sx={{ fontSize: 22 }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                {resumeProfile.file_name || "Uploaded resume"}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {`${String(resumeProfile.content_type || "").includes("pdf") ? "PDF" : "DOCX"}${resumeProfile.size ? ` · ${formatFileSize(resumeProfile.size)}` : ""}`}
                              </Typography>
                              {resumeProfile?.profile_generated_at && (
                                <Typography variant="caption" color="text.secondary">
                                  Profile generated: {formatTimestamp(resumeProfile.profile_generated_at).date} {formatTimestamp(resumeProfile.profile_generated_at).clock}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </Box>

                        <Typography variant="caption" color="text.secondary">
                          Uploading a newer resume replaces the source used for matching. Manual experience editing is no longer supported.
                        </Typography>
                      </Stack>
                    )}

                    {resumeProfile?.status === "processing_failed" && (
                      <Alert severity="error">
                        {resumeProfile.error || "Resume processing failed. Upload the resume again or try a PDF version."}
                      </Alert>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Box>
        </Paper>
      )}

      <Dialog
        open={pricingDialogOpen}
        onClose={closePricingDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pr: 6, pb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: (theme) => theme.palette.primary.main + "14",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PriceChange sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Update Pricing</Typography>
              <Typography variant="body2" color="text.secondary">
                Submit a rate or level change for admin approval.
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={closePricingDialog} sx={{ position: "absolute", right: 12, top: 12 }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.75} sx={{ mt: 1 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "background.paper",
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6 }}>
                Current pricing
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {formatMoneyFromCents(pricingProfile.currentHourlyCents, pricingProfile.currency)}/hr
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatServiceLevelSummary(pricingProfile.service, pricingProfile.level)}
              </Typography>
            </Box>

            <TextField
              select
              SelectProps={{ native: true }}
              label="Service"
              size="small"
              value={pricingDraft.service}
              onChange={(e) => {
                const nextService = e.target.value;
                const nextServiceConfig = serviceOptions.find((item) => item.code === nextService);
                const nextLevel = nextServiceConfig?.levels?.[0]?.code || "";
                const nextRate = nextServiceConfig?.levels?.[0]?.minPrice ? String(nextServiceConfig.levels[0].minPrice) : "";
                setPricingDraft((prev) => ({
                  ...prev,
                  service: nextService,
                  level: nextLevel,
                  hourlyRate: nextRate || prev.hourlyRate,
                }));
              }}
              fullWidth
            >
              <option value="">Select service</option>
              {serviceOptions.map((service) => (
                <option key={service.code} value={service.code}>
                  {service.label}
                </option>
              ))}
            </TextField>

            <TextField
              select
              SelectProps={{ native: true }}
              label="Level"
              size="small"
              value={pricingDraft.level}
              onChange={(e) => {
                const nextLevel = e.target.value;
                const levelConfig = draftLevelOptions.find((lvl) => lvl.code === nextLevel);
                setPricingDraft((prev) => ({
                  ...prev,
                  level: nextLevel,
                  hourlyRate: levelConfig?.minPrice ? String(levelConfig.minPrice) : prev.hourlyRate,
                }));
              }}
              fullWidth
              disabled={!pricingDraft.service}
            >
              <option value="">Select level</option>
              {draftLevelOptions.map((level) => (
                <option key={level.code} value={level.code}>
                  {level.label || level.code}
                </option>
              ))}
            </TextField>

            <TextField
              label="Requested hourly rate"
              size="small"
              type="number"
              value={pricingDraft.hourlyRate}
              onChange={(e) => setPricingDraft((prev) => ({ ...prev, hourlyRate: e.target.value }))}
              inputProps={{
                min: String(draftMinHourly || 0),
                max: String(draftMaxHourly || 0),
                step: "0.01",
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                endAdornment: <InputAdornment position="end">{String(draftCurrency || "usd").toUpperCase()} / hr</InputAdornment>,
              }}
              error={rateTooLow || rateTooHigh}
              helperText={
                rateTooLow
                  ? `Minimum allowed for this level is $${draftMinHourly}/hr`
                  : rateTooHigh
                    ? `Maximum allowed for this level is $${draftMaxHourly}/hr`
                    : `Allowed range: $${draftMinHourly || 0} - $${draftMaxHourly || 0} /hr`
              }
            />
            {pricingProfile.pendingRequest?.status === "pending" && (
              <Alert severity="info">
                A pending request exists. Submitting now will overwrite it.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="text"
            onClick={closePricingDialog}
            disabled={submittingPricingRequest}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitPricingRequest}
            disabled={!canRequestPricingUpdate || submittingPricingRequest}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            {submittingPricingRequest ? <Loader size={18} /> : "Submit request"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!dialogType}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pr: 6, pb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: (theme) => dialogType === "delete" ? theme.palette.error.main + "14" : theme.palette.primary.main + "14",
                color: dialogType === "delete" ? "error.main" : "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "0 0 auto",
              }}
            >
              <DialogHeaderIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{dialogTitle}</Typography>
              <Typography variant="body2" color="text.secondary">
                {dialogDescription}
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={closeDialog} sx={{ position: "absolute", right: 12, top: 12 }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {dialogType === "reauth_mfa" && mfaResolver && (
            <>
              {mfaError && (
                <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>
                  {mfaError}
                </Alert>
              )}
              {mfaInfo && (
                <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
                  {mfaInfo}
                </Alert>
              )}

              {mfaResolver.hints?.length > 1 && (
                <TextField
                  select
                  SelectProps={{ native: true }}
                  label="MFA Device"
                  fullWidth
                  margin="normal"
                  size="small"
                  value={mfaHintIndex}
                  onChange={(e) => {
                    const nextIndex = Number(e.target.value);
                    const nextHint = mfaResolver.hints?.[nextIndex];
                    setMfaHintIndex(Number(e.target.value));
                    setMfaVerificationId(nextHint?.factorId === "totp" ? "totp-ready" : "");
                    setMfaCode("");
                    setMfaInfo(nextHint?.factorId === "totp" ? "Enter the current code from your authenticator app." : "");
                    setMfaError("");
                  }}
                >
                  {mfaResolver.hints.map((hint, index) => (
                    <option key={`${hint.uid}-${index}`} value={index}>
                      {hint.displayName || hint.factorId || "MFA Device"} {hint.phoneNumber || ""}
                    </option>
                  ))}
                </TextField>
              )}

              {mfaResolver.hints?.[mfaHintIndex]?.factorId === authMod.PhoneMultiFactorGenerator.FACTOR_ID && (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {maskMfaPhone(mfaResolver.hints?.[mfaHintIndex]?.phoneNumber)}
                    </Typography>
                    <Tooltip title="Send verification code">
                      <span>
                        <IconButton onClick={sendReauthMfaCode} disabled={sendingMfaCode || saving} size="small">
                          {sendingMfaCode ? <Loader size={16} /> : <Send fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Box mt={1}>
                    <div id="profile-mfa-reauth-recaptcha" />
                  </Box>
                </>
              )}

              <TextField
                label={mfaResolver.hints?.[mfaHintIndex]?.factorId === "totp" ? "Authenticator Code" : "Verification Code"}
                fullWidth
                margin="normal"
                size="small"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                autoFocus
              />
            </>
          )}

          {!(dialogType === "reauth_mfa" && mfaResolver) && dialogStep === "verify" && dialogType !== "name" && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Enter your current password to continue. This protects sensitive account changes.
              </Alert>
              <TextField
                label="Current password"
                type="password"
                fullWidth
                size="small"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                autoFocus
              />
            </Stack>
          )}

          {(dialogStep === "edit" || dialogType === "name") && dialogType !== "reauth_mfa" && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <TextField
                label={
                  dialogType === "delete"
                    ? 'Type "delete" to confirm'
                    : dialogType === "password"
                      ? "New password"
                      : dialogType === "email"
                        ? "New email address"
                        : "Full name"
                }
                type={dialogType === "password" ? "password" : "text"}
                fullWidth
                size="small"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                autoFocus
                helperText={
                  dialogType === "delete"
                    ? "This must be typed exactly before deletion can proceed."
                    : dialogType === "password"
                      ? "Use at least 8 characters."
                      : ""
                }
              />

              {dialogType === "password" && (
                <TextField
                  label="Confirm new password"
                  type="password"
                  fullWidth
                  size="small"
                  value={confirmValue}
                  onChange={(e) => setConfirmValue(e.target.value)}
                />
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="text"
            onClick={closeDialog}
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          {dialogType === "reauth_mfa" && mfaResolver ? (
            <Button
              variant="contained"
              onClick={verifyReauthMfaCode}
              disabled={
                saving ||
                !mfaCode ||
                (
                  mfaResolver.hints?.[mfaHintIndex]?.factorId === authMod.PhoneMultiFactorGenerator.FACTOR_ID &&
                  !mfaVerificationId
                )
              }
              sx={{ textTransform: "none", fontWeight: 800 }}
            >
              {saving ? <Loader size={20} /> : dialogPrimaryLabel}
            </Button>
          ) : (
            <Button
              variant={dialogType === "delete" && dialogStep === "edit" ? "outlined" : "contained"}
              color={dialogType === "delete" && dialogStep === "edit" ? "error" : "primary"}
              onClick={dialogStep === "verify" ? verifySensitiveAction : handleSaveDialog}
              disabled={
                saving ||
                (dialogStep === "verify" && dialogType !== "name" && !passwordInput) ||
                (dialogStep === "edit" && dialogType === "delete" && inputValue.trim().toLowerCase() !== "delete") ||
                (dialogStep === "edit" && dialogType === "password" && (!inputValue || !confirmValue))
              }
              sx={{ textTransform: "none", fontWeight: 800 }}
            >
              {saving ? <Loader size={20} /> : dialogPrimaryLabel}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserProfileView;
